import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownIcon, ArrowUpIcon, Checkbox, EditIcon, Modal, Segments, useToast } from "@signet/ui";
import { CreatorAvatar, useMe, type CreatorPhotos } from "@signet/web-kit";
import { dayLabel, fetchUrgent, monthLabel, saveUrgent, subsKeys, type Urgent, type UrgentRow } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Urgent (the boss, 2026-10-13): only the top creators he picks, and how
   their week is trending against their month before. Bad lists the ones who
   need help now ("JACKIE is down heavy this week, focus on her"), Good the
   breakouts ("ARIA is killing it, double down and tell the team"). A week is
   the 7 days to the day being looked at; the month is the one before it.
   ────────────────────────────────────────────────────────────────────────── */

const name = (r: { creator: string; platform: string }) => (r.platform === "fansly" ? `${r.creator} (Fansly)` : r.creator);

function Line({ row, month, photos }: { row: UrgentRow; month: string; photos: CreatorPhotos }) {
  const bad = row.verdict === "bad";
  return (
    <li className={`subs-urgent-row ${bad ? "bad" : "good"}`}>
      <CreatorAvatar name={row.creator} src={photos(row.creator)} size={34} />
      <div className="subs-urgent-text">
        <strong>{name(row)}</strong>
        <p>
          {bad
            ? `Down ${Math.abs(row.change)}% this week against her ${month} average. She needs help now: focus on her.`
            : `Up ${row.change}% this week against her ${month} average. Double down on her and tell the team she's going viral.`}
        </p>
      </div>
      <div className="subs-urgent-figures">
        <span className={`subs-change ${bad ? "down" : "up"} tabular`}>
          {bad ? <ArrowDownIcon size={13} /> : <ArrowUpIcon size={13} />}
          {Math.abs(row.change)}%
        </span>
        <small className="tabular">
          {row.weekAvg.toLocaleString()} a day this week · {row.monthAvg.toLocaleString()} in {month}
        </small>
      </div>
    </li>
  );
}

export function UrgentView({ day, photos }: { day: string; photos: CreatorPhotos }) {
  const urgent = useQuery({ queryKey: subsKeys.urgent(day), queryFn: () => fetchUrgent(day), enabled: !!day });
  const { can } = useMe();
  const [side, setSide] = useState<"bad" | "good">("bad");
  const [picking, setPicking] = useState(false);
  const data = urgent.data;
  const month = data ? monthLabel(data.avgMonth).split(" ")[0]! : "";
  const rows = (data?.rows ?? []).filter((r) => r.verdict === side);
  const bad = data?.rows.filter((r) => r.verdict === "bad").length ?? 0;
  const good = data?.rows.filter((r) => r.verdict === "good").length ?? 0;

  return (
    <section className="panel subs-section subs-urgent">
      <div className="subs-section-head">
        <div>
          <h2>
            Urgent <span className="subs-faint">top creators, this week against {month || "last month"}</span>
          </h2>
          <p>{data ? `The 7 days to ${dayLabel(day)}. Following ${data.tracked.length} ${data.tracked.length === 1 ? "creator" : "creators"}.` : " "}</p>
        </div>
        <div className="subs-filters">
          <Segments
            label="Bad or good"
            value={side}
            options={[
              { value: "bad", label: `Bad${bad ? ` (${bad})` : ""}` },
              { value: "good", label: `Good${good ? ` (${good})` : ""}` },
            ]}
            onChange={setSide}
          />
          {can("subs.urgent:edit") && data ? (
            <button type="button" className="secondary-button" onClick={() => setPicking(true)}>
              <EditIcon size={13} /> Choose creators
            </button>
          ) : null}
        </div>
      </div>
      {urgent.isError ? <p className="subs-none">Couldn't load the Urgent list.</p> : null}
      {data && !data.tracked.length ? (
        <p className="subs-none">No top creators picked yet.{can("subs.urgent:edit") ? " Choose creators to start following them." : ""}</p>
      ) : data ? (
        rows.length ? (
          <ul className="subs-urgent-list">
            {rows.map((r) => (
              <Line key={r.id} row={r} month={month} photos={photos} />
            ))}
          </ul>
        ) : (
          <p className="subs-none">{side === "bad" ? "Nobody is down heavy this week." : "No breakouts this week."}</p>
        )
      ) : null}
      {picking && data ? <Picker data={data} photos={photos} onClose={() => setPicking(false)} /> : null}
    </section>
  );
}

function Picker({ data, photos, onClose }: { data: Urgent; photos: CreatorPhotos; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [chosen, setChosen] = useState(() => new Set(data.tracked.map((a) => a.id)));
  const save = useMutation({
    mutationFn: () => saveUrgent([...chosen]),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["subs", "urgent"] });
      toast.success("Saved", `Urgent follows ${chosen.size} ${chosen.size === 1 ? "creator" : "creators"}.`);
      onClose();
    },
    onError: (error: Error) => toast.error("Couldn't save", error.message),
  });
  const flip = (id: string) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <Modal
      open
      title="Top creators for Urgent"
      description="Pick who the Urgent tab follows."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button brass" disabled={save.isPending} onClick={() => save.mutate()}>
            Save ({chosen.size})
          </button>
        </>
      }
    >
      <div className="subs-picker">
        {data.choices.map((a) => (
          // The kit's Checkbox only carries its label for screen readers, so the name is written out
          // beside it, with the photo; the whole row toggles (the picker showed blank boxes, Jay 2026-10-13).
          <div key={a.id} className={`subs-picker-row${chosen.has(a.id) ? " on" : ""}`} onClick={() => flip(a.id)}>
            <Checkbox checked={chosen.has(a.id)} onChange={() => flip(a.id)} label={name(a)} />
            <CreatorAvatar name={a.creator} src={photos(a.creator)} size={22} />
            <span>{name(a)}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
}
